// @ts-nocheck
import React, { useMemo } from 'react';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};
const fmtPct = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v) * 100;
  return `${n.toFixed(1)}%`;
};

/**
 * Build short, opinionated key findings from the workbench bootstrap.
 * Pure derivation — no API calls.
 */
function deriveFindings(bootstrap) {
  const findings = [];
  const { adjustments = [], summary = {} } = bootstrap || {};
  const ebitda = Number(summary.ebitda_as_defined || 0);

  // 1. Total adjustments magnitude vs EBITDA.
  const total = Number(summary.total_adjustments || 0);
  if (ebitda !== 0) {
    const pct = (Math.abs(total) / Math.abs(ebitda)) * 100;
    findings.push(
      `Approved adjustments shift EBITDA by ${fmtUSD(total)} ` +
        `(${pct.toFixed(1)}% of EBITDA, as defined).`
    );
  }

  // 2. Owner-comp / management-fee concentration.
  const ownerKeywords = ['owner', 'management fee', 'h/o', 'officer', 'donation'];
  const ownerImpact = adjustments
    .filter(
      (a) =>
        a.status === 'approved' &&
        ownerKeywords.some((k) => a.source_account_name?.toLowerCase().includes(k))
    )
    .reduce(
      (acc, a) =>
        acc + Object.values(a.impact_by_period || {}).reduce((s, v) => s + Number(v || 0), 0),
      0
    );
  if (ownerImpact !== 0) {
    findings.push(
      `Owner-related adjustments account for ${fmtUSD(ownerImpact)} of the total ` +
        `(approved rows touching "owner", "management fee", "donation", etc.).`
    );
  }

  // 3. Outstanding work — Tier 2 rows still missing inputs.
  const needsInput = adjustments.filter((a) => a.status === 'needs_input').length;
  const proposed = adjustments.filter((a) => a.status === 'proposed').length;
  if (needsInput > 0) {
    findings.push(
      `${needsInput} flagged row${needsInput === 1 ? '' : 's'} require analyst input ` +
        `(replacement amount, run-rate, etc.) before they can be approved.`
    );
  }
  if (proposed > 0) {
    findings.push(
      `${proposed} row${proposed === 1 ? '' : 's'} pending review — open the Workbench to approve or reject.`
    );
  }

  // 4. By-category breakdown.
  const byCat = summary.by_category || {};
  const catPairs = Object.entries(byCat).filter(([, v]) => Number(v) !== 0);
  if (catPairs.length) {
    catPairs.sort((a, b) => Math.abs(Number(b[1])) - Math.abs(Number(a[1])));
    const [topCat, topVal] = catPairs[0];
    findings.push(`Largest approved category: ${topCat.replace(/_/g, ' ')} — ${fmtUSD(topVal)}.`);
  }

  return findings.slice(0, 5);
}

const ExecutiveOverview = ({ bootstrap }) => {
  const findings = useMemo(() => deriveFindings(bootstrap), [bootstrap]);
  if (!bootstrap) return null;
  const { summary = {}, periods = [] } = bootstrap;

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>Executive Overview</h2>
        <span className="qoe-wb-section-meta">
          {periods.length} period{periods.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="qoe-wb-kpi-row">
        <div className="qoe-wb-kpi-card">
          <div className="qoe-wb-kpi-label">Reported Revenue</div>
          <div className="qoe-wb-kpi-value">{fmtUSD(summary.reported_revenue)}</div>
          <div className="qoe-wb-kpi-sub">Sum across all periods</div>
        </div>

        <div className="qoe-wb-kpi-card">
          <div className="qoe-wb-kpi-label">EBITDA, as defined</div>
          <div className="qoe-wb-kpi-value">{fmtUSD(summary.ebitda_as_defined)}</div>
          <div className="qoe-wb-kpi-sub">NI + Interest + Tax + D&amp;A</div>
        </div>

        <div className="qoe-wb-kpi-card">
          <div className="qoe-wb-kpi-label">Diligence-Adjusted EBITDA</div>
          <div className="qoe-wb-kpi-value adjusted">{fmtUSD(summary.adjusted_ebitda)}</div>
          <div className="qoe-wb-kpi-sub">
            Net adjustments {fmtUSD(summary.total_adjustments)} (approved only)
          </div>
        </div>

        <div className="qoe-wb-kpi-card">
          <div className="qoe-wb-kpi-label">Adjusted EBITDA Margin</div>
          <div className="qoe-wb-kpi-value adjusted">{fmtPct(summary.adjusted_ebitda_margin)}</div>
          <div className="qoe-wb-kpi-sub">Adjusted EBITDA / Reported Revenue</div>
        </div>
      </div>

      {findings.length > 0 && (
        <div className="qoe-wb-findings">
          <h3>Key Findings</h3>
          <ul>
            {findings.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExecutiveOverview;
